import clsx from 'clsx'
import first from 'lodash/first'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import React, {
  Dispatch,
  FC,
  HTMLInputTypeAttribute,
  MutableRefObject,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import SvgCancel from '@Assets/SVG/Cancel'
import SvgCheck from '@Assets/SVG/Check'
import SvgEyeOff from '@Assets/SVG/EyeOff'
import SvgEyeOpen from '@Assets/SVG/EyeOpen'
import { useTranslation } from '@Globals/Translations'

import styles from './TextInput.module.scss'

export type TextInputValidationResult = { errorMessage: string, success: boolean }
export type TextInputValidationFn = (value: string) => TextInputValidationResult
export type TextInputActionProps = {
  reset: () => void
  touch: () => void
  validate: (value?: string) => boolean | undefined
}

export const useTextInputActionRef = () => {
  const ref = useRef<TextInputActionProps | null>(null)
  return ref
}

interface TextInputProps {
  actionRef?: MutableRefObject<TextInputActionProps | null>
  autoComplete?: string
  autoFocus?: boolean
  className?: string
  disabled?: boolean
  id?: string
  label: string | ReactNode
  onBlur?: (value: string, valid: boolean) => void
  onChange: (value: string, valid: boolean) => void
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  setValue?: Dispatch<SetStateAction<string>> | ((value: string) => void)
  showClearButton?: boolean
  type?: HTMLInputTypeAttribute
  validations?: Array<TextInputValidationFn>
  value: string
}

export const TextInput: FC<PropsWithChildren<TextInputProps>> = ({
  autoComplete,
  actionRef,
  className,
  disabled,
  onKeyUp,
  label,
  value,
  id,
  onBlur,
  onChange,
  showClearButton,
  validations = [],
  autoFocus,
  type = 'text',
  setValue,
}) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLInputElement | null>(null)
  const [touched, setTouched] = useState(false)
  const [errors, setErrors] = useState<TextInputValidationResult[]>([])
  const hasError = useMemo(() => touched && !isEmpty(errors), [errors, touched])
  const [isValid, setIsValid] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const typeToUse = useMemo(() => {
    if (type === 'password') {
      return (showPassword ? 'text' : type)
    }
    return type
  }, [showPassword, type])

  const runValidations: (newValue?: string) => boolean = useCallback(
    (newValue?: string) => {
      if (!isNil(newValue) && setValue) {
        setValue(newValue)
      }
      const validationErrors: Array<TextInputValidationResult> = []
      let validationSuccess: number = 0
      forEach(validations, (validationFn) => {
        const result: TextInputValidationResult = validationFn(newValue || value)
        if (!result.success) {
          validationErrors.push(result)
        }
        if (result.success) {
          validationSuccess += 1
        }
      })
      setIsValid(validationSuccess === validations.length)
      setErrors(validationErrors)
      return validationErrors.length === 0
    },
    [setValue, validations, value],
  )

  useImperativeHandle(actionRef, () => ({
    reset: () => {
      setTouched(false)
      setErrors([])
    },
    touch: () => setTouched(true),
    validate: runValidations,
  }))

  const handleChange = useCallback((newValue: string, blur: boolean) => {
    setTouched(true)
    if (blur) {
      const valid = runValidations(newValue)
      onChange(newValue, valid)
      if (onBlur) {
        onBlur(newValue, valid)
      }
      return
    }
    onChange(newValue, errors.length === 0)
  }, [errors.length, onBlur, onChange, runValidations])

  useEffect(() => {
    if (!isEmpty(value)) {
      runValidations(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={clsx(
        styles.inputContainer,
        !isEmpty(validations) && hasError && styles.error,
        !isEmpty(validations) && isValid && styles.success,
        type === 'password' && styles.withEye,
        disabled && styles.disabled,
        className,
      )}
    >
      <input
        aria-label={typeof label === 'string' ? label : undefined}
        aria-labelledby={`${id}-label`}
        autoComplete={autoComplete}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        className={clsx(styles.inputField, !isEmpty(value) && styles.inputFieldWithValue)}
        disabled={disabled}
        id={id}
        inputMode={type === 'number' ? 'numeric' : undefined}
        onBlur={e => handleChange(e.target.value, true)}
        onChange={(e) => {
          setErrors([])
          handleChange(e.target.value, false)
        }}
        onKeyUp={onKeyUp}
        ref={ref}
        type={typeToUse}
        value={value}
      />
      <label
        className={clsx(
          styles.inputLabel,
          touched && value !== '' && styles.labelUp,
        )}
        htmlFor={id}
        id={`${id}-label`}
      >
        {label}
      </label>
      {touched && showClearButton && (
        <button aria-label={t('common:CLEAR_LABEL')} className={styles.clear} onClick={() => handleChange('', true)} type="button">
          <SvgCancel />
        </button>
      )}
      {type === 'password' && (
        <button
          className={clsx(styles.passwordEye, !isEmpty(validations) && isValid && styles.valid)}
          onClick={() => setShowPassword(prev => !prev)}
          onMouseDown={event => event.preventDefault()}
          type="button"
        >
          {!showPassword && <SvgEyeOff />}
          {showPassword && <SvgEyeOpen />}
        </button>
      )}
      {!isEmpty(validations) && isValid && <div className={styles.checkmark}><SvgCheck /></div>}
      {hasError && (
        <span className={styles.errorMessage}>
          {t('common:ERROR_LABEL')}
          {': '}
          {first(errors)!.errorMessage.includes(' ') ? first(errors)!.errorMessage : t(first(errors)!.errorMessage)}
        </span>
      )}
    </div>
  )
}
